import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Rss, ExternalLink } from "lucide-react";

const TAG_STYLES = {
  CMMC: "bg-gold/20 text-gold-dark", CUI: "bg-amber-100 text-amber-800",
  NIST: "bg-blue-100 text-blue-800", SPRS: "bg-violet-100 text-violet-800",
  Cyber: "bg-red-100 text-red-800", Contracting: "bg-emerald-100 text-emerald-800",
  Defense: "bg-slate-200 text-slate-700",
};

function fmt(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }); } catch { return ""; }
}

export function DefenseFeed({ dark = false }) {
  const [state, setState] = useState({ status: "loading", items: [] });

  useEffect(() => {
    api.get("/feed/updates")
      .then((r) => setState(r.data))
      .catch(() => setState({ status: "unavailable", items: [] }));
  }, []);

  const border = dark ? "border-white/15" : "border-border";
  const titleColor = dark ? "text-white" : "text-navy";
  const sub = dark ? "text-slate-400" : "text-muted-foreground";

  return (
    <div className={`rounded-md border ${border} ${dark ? "bg-white/5" : "bg-card"} p-4`} data-testid="defense-feed">
      <div className="flex items-center gap-2 mb-1">
        <Rss className="h-4 w-4 text-gold" aria-hidden="true" />
        <h3 className={`font-display font-bold text-sm ${titleColor}`}>Defense & CMMC Updates</h3>
      </div>
      <p className={`text-[11px] ${sub} mb-3 leading-relaxed`}>
        Official updates and relevant signals for CMMC readiness, DoD contracting, cybersecurity, and defense compliance.
      </p>

      {state.status === "loading" && <p className={`text-xs ${sub}`}>Loading updates…</p>}
      {(state.status === "unavailable" || (state.status !== "loading" && state.items.length === 0)) && (
        <p className={`text-xs ${sub}`} data-testid="feed-fallback">Updates temporarily unavailable.</p>
      )}

      <ul className="space-y-2.5">
        {state.items.map((it, i) => (
          <li key={i} className={`pb-2.5 ${i < state.items.length - 1 ? `border-b ${border}` : ""}`}>
            <a href={it.link} target="_blank" rel="noreferrer"
              className={`group flex items-start gap-2 ${dark ? "text-slate-200" : "text-navy"}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${TAG_STYLES[it.tag] || "bg-slate-200 text-slate-700"}`}>{it.tag}</span>
                  <span className={`text-[10px] ${sub}`}>{it.source}{it.date ? ` · ${fmt(it.date)}` : ""}</span>
                </div>
                <p className="text-xs font-medium leading-snug group-hover:text-gold transition-colors line-clamp-2">
                  {it.title}
                </p>
              </div>
              <ExternalLink className={`h-3 w-3 shrink-0 mt-0.5 ${sub}`} />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
